import { useEffect, useState } from 'react';

import { useSelector } from 'react-redux';

import { useBankIdProfile } from '@/hooks/useBankIdProfile';
import { useRestrictedMode } from '@/hooks/useRestrictedMode';
import { selectKycDocumentsLoaded, selectKycPhotoIdApproved } from '@/store/selectors/kycSelectors';
import { selectAutoLoginFinished } from '@/store/selectors/autoLoginSelectors';
import { selectIsAuthenticated, selectIsUserTimeoutBlocked } from '@/store/selectors/authSelectors';
import {
  selectLocale,
  selectOntarioCanPlay,
  selectOntarioLocationCheckStatus,
} from '@/store/selectors/commonSelectors';
import { selectIsFastReg, selectTagsLoaded } from '@/store/selectors/userSelectors';
import { selectAmlVerificationStatus } from '@/store/selectors/amlSelectors';
import { getGameLocale, isRegulatedLocale } from '@/shared/utility';
import { envIsCanada, envIsMexico, envIsNormal, envIsSweden } from '@/constants/constants';
import { GTagEvents, triggerGTag } from '@/lib/gTagManager';
import BffApiService from '@/shared/bffApiService';
import { useBffApiRequest } from '@/hooks/useBffApiRequest';
import { BffGamesFetchGameUrlResponse } from '@lucky7ventures/bff-types';
import { BffApiError } from '@/models/bff-api-error';
import { OntarioLocationCheckStatus } from '@/models/enum/ontario-location-check-status.enum';
import { AmlVerificationStatus } from '@lucky7ventures/lucky-components';
import { MODALS } from '@/features/modal/types';
import { useModal } from '@/features/modal/useModal';

export enum GamePlayable {
  Checking = 'CHECKING',
  Yes = 'YES',
  TimeoutBlocked = 'TIMEOUT_BLOCKED',
  MissingKyc = 'MISSING_KYC',
  MissingBankIdProfile = 'MISSING_BANK_ID_PROFILE',
  HasFastRegTag = 'HAS_FAST_REG_TAG',
  AMLVerifyRequired = 'AML_VERIFY_REQUIRED',
  OntarioRestricted = 'ONTARIO_RESTRICTED',
  OntarioLocationPending = 'ONTARIO_LOCATION_PENDING',
}

// Instructions from the casino team:
// these game vendors need to use language 'es' instead of 'mx'
const mexicoESVendors = [
  'Pragmatic Play',
  'Evolution',
  'Games Global',
  'Playtech',
  'Microgaming',
  'Oryx Gaming',
  'Quickspin',
];

export const localesExcludedFromCompleteProfileRequirement = ['mx', 'th'];

export function useGameUrl(
  gameId: string,
  vendor?: string,
): {
  gameUrl: string | null;
  error: BffApiError | null;
  loading: boolean;
  playable: GamePlayable;
} {
  const { isCompletedBankIdProfile } = useBankIdProfile();
  const locale = useSelector(selectLocale);
  const photoIdApproved = useSelector(selectKycPhotoIdApproved);
  const autoLoginFinished = useSelector(selectAutoLoginFinished);
  const tagsLoaded = useSelector(selectTagsLoaded);
  const kycLoaded = useSelector(selectKycDocumentsLoaded);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isUserTimeoutBlocked = useSelector(selectIsUserTimeoutBlocked);
  const userAmlVerificationStatus = useSelector(selectAmlVerificationStatus);
  const hasFastRegTag = useSelector(selectIsFastReg);
  const [gameUrl, setGameUrl] = useState<string | null>(null);
  const [playable, setPlayable] = useState(GamePlayable.Checking);
  const { request, loading, error } = useBffApiRequest<BffGamesFetchGameUrlResponse>();
  const ontarioCanPlay = useSelector(selectOntarioCanPlay);
  const locationCheckStatus = useSelector(selectOntarioLocationCheckStatus);
  const { openModal } = useModal();
  const isRestricted = useRestrictedMode();

  const openContactInfoModal = () => {
    envIsSweden() ? openModal(MODALS.CONTACT_INFO_SWEDEN) : openModal(MODALS.CONTACT_INFO);
  };

  useEffect(() => {
    setPlayable(GamePlayable.Checking);
    if (!locale) {
      return;
    }

    if (!autoLoginFinished) {
      return;
    }

    if (isAuthenticated && isUserTimeoutBlocked) {
      setPlayable(GamePlayable.TimeoutBlocked);
      return;
    }

    // check if the user is trying to open a game on .com, coming from ontario
    if (envIsNormal() && !ontarioCanPlay) {
      setPlayable(GamePlayable.OntarioRestricted);
      return;
    }

    if (envIsCanada() && isAuthenticated) {
      // We want to wait for both kyc and tags to be loaded
      if (!kycLoaded || !tagsLoaded) {
        return;
      }

      // If the user doesn't have a photo id approved, we don't want to show the game
      if (!photoIdApproved) {
        setPlayable(GamePlayable.MissingKyc);
        return;
      }

      // if the location check has not been made we want to show the message to the user we need to check his location
      if (locationCheckStatus !== OntarioLocationCheckStatus.Done) {
        setPlayable(GamePlayable.OntarioLocationPending);
        return;
      }
    }

    // Restricted users are always play-for-fun, so skip the profile/KYC/AML gating below.
    if (isAuthenticated && !isRestricted) {
      if (!tagsLoaded) {
        // we want to hold out on decisions until the tags are loaded
        return;
      }

      if (hasFastRegTag && !localesExcludedFromCompleteProfileRequirement.includes(locale)) {
        openModal(MODALS.COMPLETE_PROFILE);
        triggerGTag(GTagEvents.complete_profile_game_click);
        setPlayable(GamePlayable.HasFastRegTag);
        return;
      }

      if (!isCompletedBankIdProfile) {
        openContactInfoModal();
        setPlayable(GamePlayable.MissingBankIdProfile);
        return;
      }

      if (
        isRegulatedLocale(locale) &&
        userAmlVerificationStatus !== null &&
        userAmlVerificationStatus !== AmlVerificationStatus.NotInProgress
      ) {
        setPlayable(GamePlayable.AMLVerifyRequired);
        return;
      }
    }

    setPlayable(GamePlayable.Yes);
    setGameUrl(null);

    const language =
      envIsMexico() && vendor && mexicoESVendors.includes(vendor) ? 'es' : getGameLocale(locale);

    request({
      apiMethod: BffApiService.getGameUrl,
      payload: {
        gameId: parseInt(gameId),
        lang: language,
        isForFun: !isAuthenticated || isRestricted,
      },
      successCallback: ({ url }) => {
        setGameUrl(url);
        triggerGTag(GTagEvents.open_game_success);
      },
      errorCallback: error => {
        triggerGTag(GTagEvents.open_game_error, { error: error.code });
      },
    });
  }, [
    locale,
    autoLoginFinished,
    kycLoaded,
    isAuthenticated,
    photoIdApproved,
    isCompletedBankIdProfile,
    tagsLoaded,
    hasFastRegTag,
    isUserTimeoutBlocked,
    userAmlVerificationStatus,
    gameId,
    ontarioCanPlay,
    locationCheckStatus,
    isRestricted,
  ]);

  return { gameUrl, loading, error, playable };
}
